import React from 'react';

import { Input, Box, PresentationalRadioGroup } from '@opendoor/bricks/core';

import { YES, NO } from 'sellDirect/components/closingDash/form/FormRadioInput';
import { IResetableFormRenderProps } from 'sellDirect/components/closingDash/ResetableForm';
import { useOfferContext } from 'sellDirect/OfferContext';
import { CustomInputLabel } from 'sellDirect/components/closingDash/form/CustomInputLabel';

interface OwnersFormProps {
  resetableForm: IResetableFormRenderProps;
  disabled: boolean;
}
const HAS_SECOND_OWNER_ID = 'hasSecondOwner';
/**
 * Form for selecting the homeowners for the SignContract flow
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const OwnersForm = ({
  resetableForm: { onUpdateMany, onUpdate, formState },
  disabled,
}: OwnersFormProps) => {
  const { hasSecondOwner, ownerName, secondOwnerName } = formState;
  const { shouldSeeAgentExperience } = useOfferContext();
  const updateSecondOwner = (newValue: string) => {
    if (newValue === NO) {
      onUpdateMany([
        { id: 'secondOwnerName', value: null },
        { id: 'hasSecondOwner', value: false },
      ]);
      return;
    }
    onUpdate('hasSecondOwner', true);
  };

  return (
    <>
      <Box data-testid="owner-on-title">
        <Input
          analyticsName="cake-closing-dash-owners-form-primary-owners-full-name"
          type="text"
          id="ownerName"
          placeholder="Owner’s full name"
          value={ownerName}
          onChange={(_event, value) => onUpdate('ownerName', value)}
          unboundedWidth
          disabled={disabled}
          label="Who’s the homeowner designated on the title?"
          required
          autoComplete="name"
        />
      </Box>

      <Box data-testid="second-owner" as={'fieldset'} border="unset" padding="0" margin="0">
        <CustomInputLabel
          id={HAS_SECOND_OWNER_ID}
          title={
            shouldSeeAgentExperience
              ? 'Is there a second person on the title?'
              : 'Is a second person on the title?'
          }
          titleFontSize="s1"
          titleFontWeight="semibold"
          required={true}
          isLegend
          mb={3}
        />

        <PresentationalRadioGroup
          analyticsName="cake-closing-contract-has-second-owner"
          name="hasSecondOwner"
          onClick={updateSecondOwner}
          selectedValue={hasSecondOwner != null ? (hasSecondOwner ? YES : NO) : undefined}
          disabled={disabled}
          options={[
            {
              label: YES,
              value: YES,
            },
            {
              label: NO,
              value: NO,
            },
          ]}
        />
      </Box>
      {hasSecondOwner && (
        <Box data-testid="second-owner-input">
          <Input
            analyticsName="cake-closing-dash-owners-form-secondary-owners-full-name"
            type="text"
            id="secondOwnerName"
            value={secondOwnerName}
            onChange={(_event, value) => onUpdate('secondOwnerName', value)}
            unboundedWidth
            disabled={disabled}
            label="Second homeowner designated on the title"
            autoComplete="name"
            required
          />
        </Box>
      )}
    </>
  );
};
